import { hideScrollbar } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { colors, spacing, typography } = themeVariables;

/* Makes the carousel the thing its own styles measure, so the auto step can ask
   how wide this rail is without asking how wide the window is. Named rather than
   anonymous so the query below cannot be captured by a container a consumer
   establishes around us, or by one added inside the carousel later.

   container-type brings layout containment with it, which makes this a containing
   block for absolutely positioned descendants. Nothing here is positioned against
   the section: every absolute in the subtree sits inside ProductCard, against the
   card. */
export const carousel = css`
  container: product-carousel / inline-size;
`;

export const header = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(${spacing.sm});
  margin-bottom: var(${spacing.sm});
`;

/* The heading and its link stack, so a long "See all women's gifts" wraps under
   the heading instead of squeezing it. */
export const headingGroup = css`
  display: flex;
  flex-direction: column;
  gap: calc(var(${spacing.xxs}) / 2);
  min-width: 0;
`;

export const heading = css`
  margin: 0;
  font-family: var(${typography.heading.fontFamily});
  font-weight: var(${typography.heading.fontWeight});
  font-size: var(${typography.heading.sizes.sm.fontSize});
  line-height: var(${typography.heading.sizes.sm.lineHeight});
  letter-spacing: var(${typography.heading.letterSpacing});
  color: var(${colors.content.default});
`;

export const subheadingLink = css`
  align-self: flex-start;
  font-family: var(${typography.body.fontFamily});
  font-size: var(${typography.body.sizes.sm.fontSize});
  line-height: var(${typography.body.sizes.sm.lineHeight});
  color: var(${colors.content.link});

  &:focus-visible {
    outline: 2px solid var(${colors.stroke.focus});
    outline-offset: 2px;
  }
`;

/* IconButton takes no className and its own css prop is spoken for, so the two
   arrows are positioned by this wrapper rather than by the buttons themselves.
   Never shrinks: the heading gives way first.

   Shown only where the device has no touchscreen, since the arrows stand in for a
   swipe rather than accompanying one. */
export const controls = css`
  display: flex;
  flex-shrink: 0;
  gap: var(${spacing.xs});

  /* A touchscreen swipes the track directly, so there the arrows are redundant
     furniture. The Ribbons carousel said the same thing with a 480px breakpoint.

     Asked as a pointer capability rather than a width, so a tablet loses them at
     any size and a narrow desktop window keeps them. Note this is the mirror of
     the ProductCard dots rule, and deliberately so: dots indicate a swipe and so
     follow the touchscreen, arrows substitute for one and so go the other way.

     display: none rather than a visual hide, so the buttons leave the
     accessibility tree too, as they did in Ribbons where the DOM was omitted. */
  @media (any-pointer: coarse) {
    display: none;
  }
`;

/* The scroll container and the list are the same element, so the track is what
   an intersection is measured against and there is no wrapper in between.

   `overscroll-behavior-x: contain` stops a swipe that reaches the end of the
   carousel from being handed to the page behind it, which on iOS is a
   back-navigation gesture. */
export const track = css`
  /* First, because it declares an overflow-y of its own that a horizontal track
     has no use for. The pair below is the one that matters. */
  ${hideScrollbar}

  display: flex;
  gap: var(--product-carousel-gap);
  margin: 0;
  padding: 0;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  list-style: none;

  /* How wide each card is and how far apart they sit, per itemSize step. Each
     step swaps the same two properties through an attribute selector rather than
     interpolating the prop, so styled-components generates and injects a single
     rule however many carousels a page renders. md is the base declaration, and
     the other two steps override it on specificity, (0,2,0) against (0,1,0),
     rather than on source order.

     Declared here rather than on the section because the track reads the gap
     itself and the items inherit the width, and because the section carries the
     ref the carousel impression depends on.

     The width and gap each step resolves to are named once below and assigned by
     the steps, rather than written out per step, because auto uses both pairs and
     duplicating the clamps is how the two drift apart.

     Every width is a clamp, and the percentage is of the track rather than of the
     viewport, so a step says how big a card should be while the carousel still
     answers to the space it has been given.

     The floor every step shares is 8.75rem, the narrowest width ProductCard is
     verified at, discounted and promoted as a sale, which is the widest its price
     row gets. Below it the card's title cuts mid-word and its partner name runs
     to a third line. The sm floor binds on a phone, unlike md's, so lowering it
     would ship a rendering state that does not occur today. Hardening the card
     further is what would let it come down. */
  --product-carousel-item-width-sm: clamp(8.75rem, 32%, 11rem);
  --product-carousel-item-width-md: clamp(8.75rem, 45%, 15rem);
  --product-carousel-gap-sm: var(${spacing.xs});
  --product-carousel-gap-md: var(${spacing.sm});

  --product-carousel-item-width: var(--product-carousel-item-width-md);
  --product-carousel-gap: var(--product-carousel-gap-md);

  &[data-item-size='sm'] {
    --product-carousel-item-width: var(--product-carousel-item-width-sm);
    --product-carousel-gap: var(--product-carousel-gap-sm);
  }

  /* auto is the small step until the carousel has the room for the larger one.
     The question is asked of the carousel's own width, through the container it
     names on its section, so this is not a breakpoint: the same carousel steps up
     in a full-width band and stays small in a narrow column, which is the whole
     point of the component knowing nothing about the viewport.

     46rem is where the rails that use this today reach at the M breakpoint, a
     768px viewport less 16px of gutter either side. That derivation belongs to
     the consumer rather than here, so read the number as a rail wide enough for
     240px cards, not as a screen size. Anything past 34.4rem is past the point
     where the small step stops growing at all.

     A browser without container queries keeps the small step at every width. That
     is smaller cards, not a broken rail, which is why the small step is the one
     declared outside the query. */
  &[data-item-size='auto'] {
    --product-carousel-item-width: var(--product-carousel-item-width-sm);
    --product-carousel-gap: var(--product-carousel-gap-sm);

    @container product-carousel (min-width: 46rem) {
      --product-carousel-item-width: var(--product-carousel-item-width-md);
      --product-carousel-gap: var(--product-carousel-gap-md);
    }
  }
`;

/* The card fills whatever it is given and takes the full height of its item, so
   constraining width here is all that is needed for a row of cards to line up.
   `align-items` is left alone on the track for the same reason: stretch is what
   makes every item as tall as the tallest.

   The width is inherited from the track, which declares it per step. No `var()`
   fallback, because the track always declares it.

   `min-width: 0` is what makes that width binding. A flex basis is a starting
   main size, not a width: an item's automatic minimum size is its content, so
   `flex: 0 0 <width>` stops a card shrinking below its step while leaving it free
   to grow above one it cannot fit. The card's image is the full width of the card
   at a fixed aspect ratio, so a card that took the extra width would take the
   extra height with it, and its partner name, title and price would sit lower
   than every neighbour's. The carousel decides how wide a card is; a card that
   cannot fit its step is card work, not a wider item. */
export const item = css`
  flex: 0 0 var(--product-carousel-item-width);
  min-width: 0;
  scroll-snap-align: start;
`;

/* Stands in for the heading while it is loading and the consumer gave none. */
export const headingSkeleton = css`
  width: 40%;
  max-width: 12rem;
  height: var(${typography.heading.sizes.sm.lineHeight});
  border-radius: 4px;
  background-color: var(${colors.surface.disabled});
`;
